import React, { useState, useEffect } from 'react';
import { teamsAPI } from '../api/client';
import { Bot, Building2, Users, Activity, CheckCircle, RefreshCw, TrendingUp } from 'lucide-react';

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function PlatformTeamsUsage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    teamsAPI.getPlatformUsage()
      .then(r => setCompanies(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const totalEnabled    = companies.reduce((s, c) => s + (c.EnabledUsers    || 0), 0);
  const totalConnected  = companies.reduce((s, c) => s + (c.ConnectedUsers  || 0), 0);
  const totalEvents     = companies.reduce((s, c) => s + (c.EventsLast30d   || 0), 0);
  const totalApprovals  = companies.reduce((s, c) => s + (c.ApprovalsLast30d|| 0), 0);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-blue-100 rounded-xl">
          <Bot className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Teams Usage — All Companies</h1>
          <p className="text-sm text-gray-500">Platform-wide Microsoft Teams bot activity</p>
        </div>
      </div>

      {/* Platform totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { icon: Users,        label: 'Enabled Users',     value: totalEnabled,   color: 'text-blue-700 bg-blue-50' },
          { icon: Activity,     label: 'Connected to Bot',  value: totalConnected, color: 'text-green-700 bg-green-50' },
          { icon: TrendingUp,   label: 'Events (30d)',       value: totalEvents,    color: 'text-purple-700 bg-purple-50' },
          { icon: CheckCircle,  label: 'Approvals (30d)',    value: totalApprovals, color: 'text-amber-700 bg-amber-50' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-3">
            <div className={`p-2.5 rounded-lg ${color}`}><Icon className="w-5 h-5" /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Company table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-center px-4 py-3">Enabled Users</th>
                <th className="text-center px-4 py-3">Bot Connected</th>
                <th className="text-center px-4 py-3">Events (30d)</th>
                <th className="text-center px-4 py-3">Approvals (30d)</th>
                <th className="text-left px-4 py-3">Last Activity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {companies.map(c => (
                <tr key={c.CompanyID} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="font-medium text-gray-900">{c.CompanyName}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${
                      c.EnabledUsers > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'
                    }`}>{c.EnabledUsers || 0}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-sm font-medium ${c.ConnectedUsers > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                      {c.ConnectedUsers || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-sm font-medium ${c.EventsLast30d > 0 ? 'text-purple-600' : 'text-gray-400'}`}>
                      {c.EventsLast30d || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-sm font-medium ${c.ApprovalsLast30d > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                      {c.ApprovalsLast30d || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">{fmt(c.LastActivity)}</td>
                </tr>
              ))}
              {!companies.length && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No data yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
